import React from "react";

import {
    ArrowForward as ArrowRightIcon,
    FiberManualRecord as DotIcon,
    TrackChanges as TrackerIcon,
} from "@mui/icons-material";
import {
    Box,
    Chip,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
} from "@mui/material";

import { useNavigate } from "react-router-dom";

interface TrackerItem {
    id: number;
    tracker_name?: string;
    status?: number;
    pivot?: {
        created_at?: string;
        [key: string]: any;
    };
}

interface LinkedTrackersProps {
    trackers?: TrackerItem[];
    onClose?: () => void;
}

const LinkedTrackers: React.FC<LinkedTrackersProps> = ({
    trackers,
    onClose,
}) => {
    const navigate = useNavigate();

    if (!trackers?.length) return null;

    const handleClick = (trackerId: number) => {
        onClose?.();
        navigate(`/tracker/${trackerId}`);
    };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return null;
        try {
            return new Date(dateStr).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
            });
        } catch {
            return null;
        }
    };

    return (
        <div
            className="source-detail-modal-card mb-3"
            style={{ marginTop: 20 }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1.5,
                }}
            >
                <h2 style={{ margin: 0 }}>Linked Trackers</h2>
                <Chip
                    label={`${trackers.length} tracker${trackers.length > 1 ? "s" : ""}`}
                    size="small"
                    sx={{
                        fontWeight: 500,
                        backgroundColor: "rgba(246, 103, 0, 0.08)",
                        color: "#f66700",
                    }}
                    onClick={() => {}}
                />
            </Box>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                This integration is currently used by the following trackers.
                Click to view tracker details.
            </Typography>
            <List disablePadding sx={{ mx: -1 }}>
                {trackers.map((tracker) => {
                    const linkedDate = formatDate(tracker.pivot?.created_at);
                    return (
                        <ListItemButton
                            key={tracker.id}
                            onClick={() => handleClick(tracker.id)}
                            sx={{
                                borderRadius: 1.5,
                                mb: 0.5,
                                py: 1,
                                px: 1.5,
                                "&:hover": {
                                    backgroundColor: "rgba(246, 103, 0, 0.04)",
                                },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                <TrackerIcon
                                    fontSize="small"
                                    sx={{ color: "#f66700" }}
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            {tracker.tracker_name ||
                                                `Tracker #${tracker.id}`}
                                        </Typography>
                                        {tracker.status !== undefined && (
                                            <DotIcon
                                                sx={{
                                                    fontSize: 10,
                                                    color:
                                                        tracker.status === 1
                                                            ? "success.main"
                                                            : "grey.400",
                                                }}
                                            />
                                        )}
                                    </Box>
                                }
                                secondary={
                                    linkedDate
                                        ? `Linked ${linkedDate}`
                                        : `ID: ${tracker.id}`
                                }
                                secondaryTypographyProps={{
                                    variant: "caption",
                                    sx: { color: "text.disabled" },
                                }}
                            />
                            <ArrowRightIcon
                                fontSize="small"
                                sx={{ color: "text.disabled", ml: 1 }}
                            />
                        </ListItemButton>
                    );
                })}
            </List>
        </div>
    );
};

export default LinkedTrackers;
